import { useCallback, useContext, useMemo } from 'react';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { TEST_FLOW, usePrescriptionStore } from '../../../stores/prescriptionStore';
import { SolutiAuth } from './certifiers/Soluti/Soluti';
import { ReactComponent as ActivateCertificate } from '../../../assets/images/certificates/activate-certificate.svg';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import PrescriptionSigningFlows from '../../../components/PrescriptionSigningFlows';
import { DialogWrappedComponentProps, FeedbackContext } from '../../../providers/FeedbackProvider';
import LocalCertificateFlow from '../../../components/PrescriptionSigningFlows/LocalCertificateFlow';
import { CERTIFICATES } from '../../certificate/certificates';
import { Tracker } from '../../../shared/tracker/Tracker';
import { useBreakpointValue } from '@chakra-ui/react';
import { TrackerEventService } from '../trackerEventService';

interface InitialCertificateFlowProps {
  onCancel(): void;
  onComplete(): void;
  testFlow: TEST_FLOW.A | TEST_FLOW.B;
}

export const InitialCertificateFlow = (props: InitialCertificateFlowProps) => {
  const { testFlow, onCancel, onComplete } = props;

  const prescription = usePrescriptionStore((store) => store.prescription);
  const ownedCertificates = usePrescriptionStore((store) => store.ownedCertificates);

  const userOwnedCertificates = ownedCertificates
    .filter((certificate) => certificate.status === 'S')
    .map((certificate) => certificate.name);

  const userOnlyHasSoluti = userOwnedCertificates.length === 1 && userOwnedCertificates[0] === 'SOLUTI';

  const { prescriberConfigurations, setSelectedCertificateType } = usePrescriptionStore();

  const { openBaseDialog } = useContext(FeedbackContext);
  const dialogWidth = useBreakpointValue({ base: '95%', md: '575px' });

  const openLocalSignatureDialog = useCallback(() => {
    return openBaseDialog(
      (dialogProps: DialogWrappedComponentProps) => <LocalCertificateFlow onClose={dialogProps.onClose} />,
      {
        width: dialogWidth,
        height: 'auto',
        persist: false,
      }
    );
  }, [dialogWidth, openBaseDialog]);

  const onSelectCertificate = useCallback(
    async (value: CERTIFICATES) => {
      if (!prescription) return;

      Tracker.trackEvent({
        event: 'Type of Signature Selected',
        parameters: {
          'CD Selected': value,
        },
      });

      if (value === CERTIFICATES.LOCAL) return openLocalSignatureDialog();

      const testFlow = prescriberConfigurations?.Configuracoes?.signatureRaffleFlow || '';

      await TrackerEventService.track(prescription.idPrescricao, 'certificates-ab-test', {
        fluxo: testFlow,
        assinaturaDigital: value !== CERTIFICATES.MANUAL,
        idMedico: prescription.idPrescritor,
      });

      setSelectedCertificateType(value);
    },
    [
      openLocalSignatureDialog,
      prescriberConfigurations?.Configuracoes?.signatureRaffleFlow,
      prescription,
      setSelectedCertificateType,
    ]
  );

  const SignatureFlow = useMemo(() => {
    if (!prescription) return;

    if (userOnlyHasSoluti) {
      return (
        <SolutiAuth
          prescriptionId={prescription.idPrescricao}
          prescriberId={prescription.idPrescritor}
          onCancel={onCancel}
          testFlow={testFlow}
          onComplete={onComplete}
        />
      );
    }

    return <PrescriptionSigningFlows onSelectCertificate={onSelectCertificate} />;
  }, [prescription, onCancel, userOnlyHasSoluti, testFlow, onComplete, onSelectCertificate]);

  const title = props.testFlow === TEST_FLOW.A ? 'A assinatura digital é obrigatória!' : 'Ative seu certificado agora!';
  const subtTitle =
    props.testFlow === TEST_FLOW.A
      ? 'Você ativa seu certificado digital hoje, e fará o processo novamente apenas daqui 7 dias. Os documentos emitidos serão <b> enviados digitalmente para o celular do seu paciente!</b>'
      : '<b>Tenha mais segurança, agilidade na emissão e reduza custos com impressão.</b> </br> Documentos médicos com assinatura digital são válidos e são enviados pela Mevo para o celular do seu paciente.';

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      p={'30px'}
      borderRadius={'20px'}
      backgroundColor={'#fff'}
      maxW={'800px'}
    >
      <Flex justifyContent={'center'} mb={'15px'}>
        <ActivateCertificate />
      </Flex>

      <Text fontSize={'30px'} fontWeight={700} mb={'12px'}>
        {title}
      </Text>
      <Text fontSize={'14px'} lineHeight={'16px'} mb={'12px'} dangerouslySetInnerHTML={{ __html: subtTitle }}></Text>

      <Box>{SignatureFlow}</Box>
    </Box>
  );
};
